import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { add, format, startOfDay } from 'date-fns';
import { sort } from 'remeda';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { LmoFracModalService } from '~lmo/services/lmo-frac-modal.service';
import { LmoLoadersService, MineCloseTime } from '~lmo/services/lmo-loaders.service';
import { trackById } from '~utilities/trackById';

function openAfterClose(control: FormGroup): { [key: string]: any } | null {
  const closeAfterOpenCheck = control.get('closeMinuteOfDay').value > control.get('openMinuteOfDay').value;
  return closeAfterOpenCheck ? { closeAfterOpen: 'Open must be after Close' } : null;
}

const formatString = 'HH:mm';

export interface TimeLineValue {
  closeMinuteOfDay: number;
  closeValueString: string;
  openMinuteOfDay: number;
  openValueString: string;
}

@Component({
  selector: 'sa-lmo-loader-closed-schedule-specific-loader',
  styleUrls: ['./lmo-loader-closed-schedule-specific-loader.component.scss'],
  templateUrl: './lmo-loader-closed-schedule-specific-loader.component.html',
})
export class LmoLoaderClosedScheduleSpecificLoaderComponent implements OnInit, OnDestroy {
  private destroy$$ = new Subject();
  public trackById = trackById;
  public closedTimes: { name: string; times: FormArray }[] = [
    {
      name: 'sunday',
      times: new FormArray([]),
    },
    {
      name: 'monday',
      times: new FormArray([]),
    },
    {
      name: 'tuesday',
      times: new FormArray([]),
    },
    {
      name: 'wednesday',
      times: new FormArray([]),
    },
    {
      name: 'thursday',
      times: new FormArray([]),
    },
    {
      name: 'friday',
      times: new FormArray([]),
    },
    {
      name: 'saturday',
      times: new FormArray([]),
    },
  ];
  constructor(
    private loaderService: LmoLoadersService,
    private fb: FormBuilder,
    private lmoModal: LmoFracModalService,
  ) {}

  public ngOnInit() {
    this.loaderService.mineClosedTimes$.pipe(takeUntil(this.destroy$$)).subscribe((times) => {
      this.clearArrays();
      this.setArrays(times);
    });
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public addLine(dayIndex: number, closeMinuteOfDayNumber?: number, openMinuteOfDayNumber?: number) {
    const closeDate =
      closeMinuteOfDayNumber !== undefined
        ? add(startOfDay(new Date()), { minutes: closeMinuteOfDayNumber })
        : new Date();
    const openDate =
      openMinuteOfDayNumber !== undefined
        ? add(startOfDay(new Date()), { minutes: openMinuteOfDayNumber })
        : add(closeDate, { minutes: 1 });
    const closeValueString = format(closeDate, formatString);
    const closeMinuteOfDay = getMinutesInDayFromHoursAndMinutes(closeValueString);
    const openValueString = format(openDate, formatString);
    const openMinuteOfDay = getMinutesInDayFromHoursAndMinutes(openValueString);
    const group = this.fb.group({
      closeMinuteOfDay: [closeMinuteOfDay],
      closeValueString: [closeValueString],
      openMinuteOfDay: [openMinuteOfDay],
      openValueString: [openValueString],
    });
    group.setValidators([openAfterClose]);
    group
      .get('closeValueString')
      .valueChanges.pipe(startWith(group.get('closeValueString').value))
      .subscribe((str: string) => {
        group.get('closeMinuteOfDay').setValue(getMinutesInDayFromHoursAndMinutes(str));
      });
    group
      .get('openValueString')
      .valueChanges.pipe(startWith(group.get('openValueString').value))
      .subscribe((str: string) => {
        const value = getMinutesInDayFromHoursAndMinutes(str);
        group.get('openMinuteOfDay').setValue(value !== 0 ? value : 60 * 24);
      });
    this.closedTimes[dayIndex].times.push(group);
  }

  public removeLine(dayIndex: number, index: number) {
    this.closedTimes[dayIndex].times.removeAt(index);
  }

  public getTimes(): MineCloseTime[] {
    const mineCloseTimes: MineCloseTime[] = [];
    this.closedTimes.forEach((time, index) => {
      (time.times.value as TimeLineValue[]).forEach((entry) => {
        const mineCloseTime: MineCloseTime = {
          closeMinuteOfDay: entry.closeMinuteOfDay,
          daysSinceSunday: index,
          openMinuteOfDay: entry.openMinuteOfDay,
        };
        if (mineCloseTime.openMinuteOfDay === 0) {
          mineCloseTime.openMinuteOfDay = 24 * 60;
        }
        mineCloseTimes.push(mineCloseTime);
      });
    });
    return mineCloseTimes;
  }

  public saveChanges() {
    const times = this.getTimes();
    this.loaderService.setMineClosedTimes(times).then((r) => {
      this.lmoModal.closeHomeModal();
    });
  }

  private clearArrays() {
    this.closedTimes.forEach((t) => {
      while (t.times.length) {
        t.times.removeAt(0);
      }
    });
  }

  private setArrays(times: MineCloseTime[]) {
    const sortedTimes = sort(times, sortTimes);
    sortedTimes.forEach((time) => {
      this.addLine(time.daysSinceSunday, time.closeMinuteOfDay, time.openMinuteOfDay);
    });
  }
}

function sortTimes(a: MineCloseTime, b: MineCloseTime): number {
  if (a.daysSinceSunday === b.daysSinceSunday) {
    return a.closeMinuteOfDay - b.closeMinuteOfDay;
  }
  return a.daysSinceSunday - b.daysSinceSunday;
}

function getMinutesInDayFromHoursAndMinutes(time: string): number {
  const split = time.split(':');
  if (split.length !== 2) {
    return 0;
  }
  return +split[0] * 60 + +split[1];
}
